import { useState } from 'react';
import { Box, Button, Link, TextField, Typography } from '@mui/material';
import { DEAL_PERMITTED_STAGES } from 'common/constants/analyst-dashboards';
import { ConditionalLoader } from 'dashboard409a/pages/Operations/components';
import formatCurrency from 'dashboard409a/utilities/formatCurrency';
import theme from 'theme';
import { DealStageState, ValuationDealInfoProps } from './ValuationDealInfo.props';
import {
  DealNameContainer,
  Row,
  StyledAutocomplete,
  StyledLinkSpan,
  Value,
} from './ValuationDealInfo.styles';

interface DealStageOptionItem {
  id: string;
  label: string;
}

interface RawDealStageOption {
  id: { status_id: string };
  title: string;
}

const ValuationDealInfo = ({
  deal,
  isLoading,
  onDealStageChange,
  onSendFinalInvoice,
  isFromModels,
}: ValuationDealInfoProps) => {
  const [dealStageState, setDealStageState] = useState<DealStageState>(DealStageState.EDITING);

  const handleDealStageChange = async (value: DealStageOptionItem | null) => {
    if (!value) {
      return;
    }
    setDealStageState(DealStageState.UPDATING);
    try {
      await onDealStageChange(value.id);
    } finally {
      setDealStageState(DealStageState.EDITING);
    }
  };

  const mappedOptions =
    (deal?.dealStageOptions as RawDealStageOption[] | undefined)?.map(option => ({
      id: option.id.status_id,
      label: option.title,
    })) || [];

  const allDealStageOptions: DealStageOptionItem[] = mappedOptions.toSorted(
    (a, b) => DEAL_PERMITTED_STAGES.indexOf(b.label) - DEAL_PERMITTED_STAGES.indexOf(a.label)
  );

  let engagementLetterLink = null;
  if (deal?.signedPDF) {
    engagementLetterLink = (
      <Link href={deal.signedPDF} target="_blank" rel="noopener noreferrer">
        {' Signed EL'}
      </Link>
    );
  } else if (deal?.elURL) {
    engagementLetterLink = (
      <Link href={deal.elURL} target="_blank" rel="noopener noreferrer">
        {' EL'}
      </Link>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#08C581', padding: 1, borderRadius: 1, gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ color: theme.palette.white, fontSize: '1.25rem', fontWeight: 600 }}>Deal</Typography>
        <ConditionalLoader isLoading={dealStageState === DealStageState.UPDATING} width={150}>

          <StyledAutocomplete
            value={allDealStageOptions.find(option => option.label === deal?.dealStage) || null}
            options={allDealStageOptions}
            getOptionDisabled={option => !DEAL_PERMITTED_STAGES.includes((option as DealStageOptionItem).label)}
            renderInput={params => <TextField {...params} variant="outlined" size="small" />}
            disableClearable
            onChange={(_event, value) => {
              handleDealStageChange(value as DealStageOptionItem | null);
            }}
            renderOption={(props, option) => {
              const typedOption = option as DealStageOptionItem;
              return (
                <li
                  {...props}
                  key={typedOption.id}
                  style={{
                    fontSize: '0.75rem',
                  }}>
                  {typedOption.label}
                </li>
              );
            }}
          />
        </ConditionalLoader>
      </Box>
      <DealNameContainer>
        <ConditionalLoader isLoading={isLoading && !deal?.dealName} width={120}>
          <Link href={deal?.dealUrl} target="_blank" rel="noopener noreferrer">
            <StyledLinkSpan>{deal?.dealName || '-'}</StyledLinkSpan>
          </Link>
        </ConditionalLoader>
        {deal?.dealNotice && <span>NOTICE: {deal.dealNotice}</span>}
      </DealNameContainer>

      <Row>
        <ConditionalLoader isLoading={isLoading && deal?.net === undefined} width={80}>
          <Value>NET: {formatCurrency({ value: deal?.net || '-' })}</Value>
        </ConditionalLoader>
        <ConditionalLoader isLoading={isLoading && !deal?.due} width={80}>
          <Value>Due: {deal?.due || '-'}</Value>
        </ConditionalLoader>
      </Row>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <ConditionalLoader isLoading={isLoading && deal?.contractStatus === undefined} width={120}>
          <Value>Contract: {deal?.contractStatus || '-'}</Value>
          {engagementLetterLink}
        </ConditionalLoader>
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }}
          onClick={onSendFinalInvoice}
          disabled={dealStageState === DealStageState.UPDATING || isLoading || isFromModels}
          id="send-final-invoice-button">
                Send Final Invoice
        </Button>
      </Box>
    </Box>
  );
};

export default ValuationDealInfo;
